import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  ProviderMode,
  ProviderSummary,
  ProviderUpsertRequest,
} from '../../model/settingsTypes';

export interface ProviderFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial: ProviderSummary | null;
  canManageCredentials: boolean;
  credentialHint?: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (body: ProviderUpsertRequest) => void;
}

/** Create/edit form for a delivery, payment, or notification provider. */
export function ProviderFormDialog({
  open,
  mode,
  initial,
  canManageCredentials,
  credentialHint,
  saving,
  onClose,
  onSubmit,
}: ProviderFormDialogProps) {
  const { t } = useTranslation('settings');
  const MODE_OPTIONS: { value: ProviderMode; label: string }[] = [
    { value: 'SANDBOX', label: t('providerFormDialog.modeOptions.sandbox') },
    { value: 'PRODUCTION', label: t('providerFormDialog.modeOptions.production') },
  ];
  const resolvedCredentialHint = credentialHint ?? t('providerFormDialog.defaultCredentialHint');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState<string>('');
  const [providerMode, setProviderMode] = useState<ProviderMode>('SANDBOX');
  const [secret, setSecret] = useState('');
  const [settingsText, setSettingsText] = useState('');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(initial?.code ?? '');
      setName(initial?.name ?? '');
      setEnabled(initial?.enabled ?? true);
      setPriority(initial?.priority != null ? String(initial.priority) : '');
      setProviderMode(initial?.mode ?? 'SANDBOX');
      setSecret('');
      setSettingsText('');
      setSettingsError(null);
      setSubmitted(false);
    }
  }, [open, initial]);

  const codeError = submitted && code.trim() === '';
  const nameError = submitted && name.trim() === '';

  const handleSubmit = () => {
    setSubmitted(true);
    if (code.trim() === '' || name.trim() === '') {
      return;
    }

    let parsedSettings: Record<string, unknown> | undefined;
    if (settingsText.trim() !== '') {
      try {
        parsedSettings = JSON.parse(settingsText) as Record<string, unknown>;
      } catch {
        setSettingsError(t('providerFormDialog.settingsInvalid'));
        return;
      }
    }

    const priorityValue = priority.trim() === '' ? null : Number(priority);

    const body: ProviderUpsertRequest = {
      code: code.trim(),
      name: name.trim(),
      enabled,
      priority: priorityValue,
      mode: providerMode,
      ...(canManageCredentials && secret.trim() !== '' ? { secret: secret } : {}),
      ...(parsedSettings ? { settings: parsedSettings } : {}),
    };

    onSubmit(body);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'create' ? t('providerFormDialog.addTitle') : t('providerFormDialog.editTitle')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('providerFormDialog.codeLabel')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={codeError}
            helperText={
              codeError ? t('providerFormDialog.codeRequired') : t('providerFormDialog.codeHelper')
            }
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('providerFormDialog.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={nameError}
            helperText={nameError ? t('providerFormDialog.nameRequired') : undefined}
            size="small"
            fullWidth
            required
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('providerFormDialog.priorityLabel')}
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
              type="number"
              size="small"
              fullWidth
              helperText={t('providerFormDialog.priorityHelper')}
            />
            <TextField
              label={t('providerFormDialog.modeLabel')}
              value={providerMode}
              onChange={(event) => setProviderMode(event.target.value as ProviderMode)}
              select
              size="small"
              fullWidth
            >
              {MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {canManageCredentials ? (
            <TextField
              label={t('providerFormDialog.secretLabel')}
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              type="password"
              size="small"
              fullWidth
              autoComplete="new-password"
              placeholder={
                initial?.credential?.present
                  ? t('providerFormDialog.secretPlaceholderUnchanged')
                  : ''
              }
              helperText={t('providerFormDialog.secretHelper', { hint: resolvedCredentialHint })}
            />
          ) : null}

          <TextField
            label={t('providerFormDialog.settingsLabel')}
            value={settingsText}
            onChange={(event) => {
              setSettingsText(event.target.value);
              setSettingsError(null);
            }}
            error={Boolean(settingsError)}
            helperText={settingsError ?? t('providerFormDialog.settingsHelper')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={
              <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            }
            label={t('providerFormDialog.enabledLabel')}
          />

          {!canManageCredentials ? (
            <Alert severity="info">{t('providerFormDialog.credentialsReadOnlyNotice')}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mode === 'create' ? t('providerFormDialog.addTitle') : t('providerFormDialog.submitSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
